import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlarmClock, Calendar, CheckSquare, Hash, List, Plus, Type, X } from 'lucide-react';
import { addProperty, isSchemaKey } from '@nexus/core';
import { parentDatabase, saveSchema } from '@nexus/workspace';
import { RowProperties } from './database/RowProperties';
import { DateTimeEditor, ReminderEditor } from './database/values';
import { useStore, useStoreQuery } from '../db/context';
import { cn } from '../lib/cn';

type Kind = 'text' | 'number' | 'checkbox' | 'date' | 'list' | 'reminder';

/** A date, optionally with a time ("2026-10-05", "2026-10-05T14:00", ISO with seconds/zone). */
const DATE_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T ]\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
/** Keys that are always lists (Obsidian conventions). */
const LIST_KEYS = new Set(['tags', 'aliases', 'cssclasses']);
/** Keys that hold a date, also while still empty. */
const DATE_KEYS = new Set([
  'due',
  'due date',
  'deadline',
  'date',
  'next due',
  'scheduled',
  'start',
  'done',
  'completed',
]);
/** Task page reminders: a moment, or a time before the due date. */
const REMINDER_KEYS = new Set(['reminder', 'herinnering']);

function kindOf(key: string, value: unknown): Kind {
  const k = key.toLowerCase();
  if (REMINDER_KEYS.has(k) && !Array.isArray(value)) return 'reminder';
  if (Array.isArray(value) || LIST_KEYS.has(k)) return 'list';
  if (typeof value === 'boolean') return 'checkbox';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string' && DATE_RE.test(value.trim())) return 'date';
  if (DATE_KEYS.has(k) && (value == null || value === '')) return 'date';
  return 'text';
}

const ICONS: Record<Kind, typeof Type> = {
  text: Type,
  number: Hash,
  checkbox: CheckSquare,
  date: Calendar,
  list: List,
  reminder: AlarmClock,
};

const input =
  'min-w-0 flex-1 rounded px-1.5 py-0.5 bg-transparent hover:bg-bg-hover focus:bg-bg-hover outline-none';

function ListEditor({
  value,
  onChange,
  label,
}: {
  value: unknown[];
  onChange: (v: string[]) => void;
  label: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const items = value.map(String);
  const add = () => {
    const next = draft
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (next.length) onChange([...items, ...next.filter((n) => !items.includes(n))]);
    setDraft('');
  };
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
      {items.map((item) => (
        <span
          key={item}
          className="flex items-center gap-0.5 rounded-full bg-bg-active px-2 py-0.5 text-xs"
        >
          {item}
          <button
            type="button"
            aria-label={t('props.removeValue', { value: item })}
            onClick={() => onChange(items.filter((i) => i !== item))}
            className="rounded-full text-fg-muted hover:text-fg"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        aria-label={label}
        placeholder={items.length ? '' : t('props.addValue')}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={add}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !draft && items.length) {
            onChange(items.slice(0, -1));
          }
        }}
        className={cn(input, 'min-w-24 text-sm')}
      />
    </div>
  );
}

/** The same property values with no editor -- a read-only workspace membership. */
function ReadOnlyValue({ name, value }: { name: string; value: unknown }) {
  const kind = kindOf(name, value);
  switch (kind) {
    case 'list': {
      const items = (Array.isArray(value) ? value : value == null || value === '' ? [] : [value])
        .map(String)
        .filter(Boolean);
      if (items.length === 0) return <span className="px-1.5 text-sm text-fg-muted" />;
      return (
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {items.map((item) => (
            <span key={item} className="rounded-full bg-bg-active px-2 py-0.5 text-xs">
              {item}
            </span>
          ))}
        </span>
      );
    }
    case 'checkbox':
      return <span className="px-1.5 text-sm">{value === true ? '✓' : '—'}</span>;
    default:
      return (
        <span className="min-w-0 flex-1 truncate px-1.5 text-sm">
          {value == null || value === ''
            ? ''
            : typeof value === 'object'
              ? JSON.stringify(value)
              : String(value)}
        </span>
      );
  }
}

function ValueEditor({
  name,
  value,
  onChange,
}: {
  name: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const kind = kindOf(name, value);
  const label = name;
  switch (kind) {
    case 'list':
      return (
        <ListEditor
          label={label}
          value={Array.isArray(value) ? value : value == null || value === '' ? [] : [value]}
          onChange={onChange}
        />
      );
    case 'checkbox':
      return (
        <input
          type="checkbox"
          aria-label={label}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4 accent-[var(--accent)]"
        />
      );
    case 'date':
      return <DateTimeEditor label={label} value={String(value ?? '')} onChange={onChange} />;
    case 'reminder':
      return <ReminderEditor label={label} value={String(value ?? '')} onChange={onChange} />;
    case 'number':
      return (
        <input
          type="number"
          aria-label={label}
          defaultValue={String(value)}
          onBlur={(e) => {
            const n = e.target.value === '' ? null : Number(e.target.value);
            if (n !== value) onChange(n);
          }}
          className={cn(input, 'text-sm')}
        />
      );
    default:
      return (
        <input
          aria-label={label}
          defaultValue={
            value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value)
          }
          onBlur={(e) => e.target.value !== (value ?? '') && onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          className={cn(input, 'text-sm')}
        />
      );
  }
}

export function PropertiesPanel({ pageId, readOnly }: { pageId: string; readOnly?: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const data = useStoreQuery(
    async (s) => {
      const page = await s.getPage(pageId);
      return { props: await s.getProps(pageId), db: page ? await parentDatabase(s, page) : null };
    },
    [pageId],
    ['props', 'pages'],
  );
  const [adding, setAdding] = useState(false);
  if (!data) return null;
  const { props, db } = data;
  const hiddenKeys = new Set(Object.keys(db?.schema.rowDefaults ?? {}).map((k) => k.toLowerCase()));
  // Database rows show their schema properties above; Nexus keeps its own data hidden.
  const entries = Object.entries(props).filter(
    ([key]) =>
      !key.startsWith('nexus.') &&
      !(db && (isSchemaKey(db.schema, key) || hiddenKeys.has(key.toLowerCase()))),
  );

  const addKey = (key: string) => {
    setAdding(false);
    const k = key.trim();
    if (!k) return;
    // On a database row a new property belongs to the database.
    if (db) {
      const known =
        isSchemaKey(db.schema, k) ||
        db.schema.properties.some((p) => p.name.toLowerCase() === k.toLowerCase());
      if (!known) void saveSchema(store, db.id, addProperty(db.schema, k).schema);
      return;
    }
    if (!(k in props)) void store.setProp(pageId, k, LIST_KEYS.has(k.toLowerCase()) ? [] : '');
  };

  return (
    <section aria-label={t('props.title')} className="mt-4 text-sm">
      {props['nexus.template'] === true && (
        <p className="mb-2 rounded-md bg-bg-subtle px-2 py-1 text-xs text-fg-muted">
          {t('db.templateBanner')}
        </p>
      )}
      {db && <RowProperties pageId={pageId} props={props} db={db} readOnly={readOnly} />}
      {entries.length > 0 && (
        <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-2 gap-y-0.5">
          {entries.map(([key, value]) => {
            const Icon = ICONS[kindOf(key, value)];
            return (
              <div key={key} className="group contents">
                <dt className="flex items-center gap-1.5 py-0.5 text-fg-muted">
                  <Icon className="size-3.5 shrink-0" aria-hidden />
                  <span className="truncate">{key}</span>
                </dt>
                <dd className="flex min-w-0 items-center gap-1">
                  {readOnly ? (
                    <ReadOnlyValue name={key} value={value} />
                  ) : (
                    <>
                      <ValueEditor
                        // Date/reminder editors keep their own state; the others are
                        // uncontrolled inputs that restart from the stored value.
                        key={
                          ['date', 'reminder'].includes(kindOf(key, value))
                            ? key
                            : `${key}:${JSON.stringify(value)}`
                        }
                        name={key}
                        value={value}
                        onChange={(v) => void store.setProp(pageId, key, v)}
                      />
                      <button
                        type="button"
                        aria-label={t('props.remove', { key })}
                        onClick={() => void store.setProp(pageId, key, undefined)}
                        className="rounded p-0.5 text-fg-muted opacity-0 group-hover:opacity-100 hover:bg-bg-hover focus:opacity-100"
                      >
                        <X className="size-3.5" />
                      </button>
                    </>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      {!readOnly &&
        (adding ? (
          <input
            autoFocus
            aria-label={t('props.newName')}
            placeholder={t('props.newName')}
            list="nx-prop-suggestions"
            onBlur={(e) => addKey(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addKey(e.currentTarget.value);
              if (e.key === 'Escape') setAdding(false);
            }}
            className="mt-1 rounded-md border border-border bg-bg px-2 py-0.5"
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="mt-1 flex items-center gap-1 rounded px-1 py-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
          >
            <Plus className="size-3.5" />
            {t('props.add')}
          </button>
        ))}
      <datalist id="nx-prop-suggestions">
        {[
          'tags',
          'aliases',
          'type',
          'status',
          'priority',
          'project',
          'due',
          'reminder',
          'owner',
        ].map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>
    </section>
  );
}
